import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ViewEncapsulation, output, inject, input, model, effect } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { ApButtonComponent } from '@ap/button';
import { ApContentModule, ApContentService } from '@ap/content';
import { ApCheckboxComponent } from '@ap/checkbox';
import { ApIconButtonLinkComponent } from '@ap/icon-link';

import { ApSelectorOption } from '../../selector.modal';
import { ApSelectorModalGroup } from '../selector-modal.model';

import content from './selector-modal-multiple.content.json';

@Component({
    selector: 'ap-selector-modal-multiple',
    templateUrl: './selector-modal-multiple.component.html',
    styleUrls: ['../selector-modal.generic.scss', './selector-modal-multiple.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        NgClass,
        NgTemplateOutlet,
        ApButtonComponent,
        ApCheckboxComponent,
        ApContentModule,
        ApIconButtonLinkComponent,
        ReactiveFormsModule
    ]
})
export class ApSelectorModalMultipleComponent {
    private readonly contentService = inject(ApContentService);

    /**
     * Array of Selector options
     */
    public readonly optionsToDisplay = input<ApSelectorOption[]>(undefined);

    /**
     * Array of Selector options
     */
    public readonly options = input<ApSelectorOption[]>(undefined);

    /**
     * Currently selected options
     */
    public readonly selectedOptions = model<ApSelectorOption[]>(undefined);

    /**
     * Optional grouping of items
     */
    public readonly groups = input<ApSelectorModalGroup[]>(undefined);

    /**
     * If provided, this function will trigger when the item is closed
     */
    public readonly selected = output<ApSelectorOption[]>();

    protected form: FormGroup;

    constructor() {
        this.contentService.addContentItems(content);

        effect(() => {
            const optionsToDisplay = this.optionsToDisplay();
            this.initializeForm(optionsToDisplay);
        });
    }

    /**
     * Get FormControl by option id
     */
    public getFormControl(optionId: string): FormControl {
        return this.form.controls[optionId] as FormControl;
    }

    /**
     * On user interaction checkbox change add or remove che optiom from selected options
     */
    public onChange(event: Event, option: ApSelectorOption): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        if (!isChecked) {
            this.selectedOptions.set(
                this.selectedOptions()
                    .slice(0)
                    .filter((selectedOption) => selectedOption.id !== option.id)
            );
            return;
        }
        this.selectedOptions().push(option);
    }

    /**
     * Select all options
     */
    public selectAll(): void {
        this.selectedOptions.set(this.options().slice(0));
        this.updateCheckboxes(true);
    }

    /**
     * Deselect all options
     */
    public deselectAll(): void {
        this.selectedOptions.set([]);
        this.updateCheckboxes(false);
    }

    /**
     * Select an option and close modal
     */
    protected select(): void {
        this.selected.emit(this.selectedOptions());
    }

    /**
     * Get filtered options of group
     */
    protected getGroupOptions(groupId: number): ApSelectorOption[] {
        return this.optionsToDisplay().filter((option) => option.groupId === groupId);
    }

    /**
     * Initiliaze form. Create form control for every option
     */
    private initializeForm(optionsToDisplay: ApSelectorOption[]): void {
        this.form = new FormGroup({});

        const selectedOptionIds = this.selectedOptions()?.map((selectedOption) => selectedOption.id) || [];

        optionsToDisplay?.forEach((option) => {
            this.form.addControl(option.id, new FormControl(selectedOptionIds.indexOf(option.id) > -1));
        });
    }

    /**
     * Update checkbox state
     */
    private updateCheckboxes(checked: boolean): void {
        Object.keys(this.form.controls).forEach((key) => {
            const control = this.form.controls[key];
            control.setValue(checked);
        });

        this.form.updateValueAndValidity();
    }
}
